import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import {
  WorkPaperTemplate,
  ListTemplatesResponseSchema,
  DuplicateTemplateResponseSchema,
  MessageResponseSchema,
  TemplateType,
} from "../models/workPaperTemplate";

interface UseWorkPaperTemplatesOptions {
  // Omitted, both kinds are listed (the templates list page); a session passes
  // its own kind.
  type?: TemplateType;
  fetchOnLoad?: boolean;
}

export function useWorkPaperTemplates({
  type,
  fetchOnLoad = true,
}: UseWorkPaperTemplatesOptions = {}) {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [templates, setTemplates] = useState<WorkPaperTemplate[]>([]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        // In the path, not `query`: fetchFastApi's token-refresh retry drops `query`.
        path: type
          ? `/work-paper-templates/?type=${encodeURIComponent(type)}`
          : "/work-paper-templates/",
        method: "GET",
      });

      const parsed = ListTemplatesResponseSchema.parse(response);
      setTemplates(parsed.templates);
    } catch (error) {
      console.error("Error fetching workpaper templates:", error);
      toast.error("Failed to load workpaper templates");
    } finally {
      setLoading(false);
    }
  }, [type]);

  const duplicateTemplate = useCallback(
    async (templateId: string): Promise<string | null> => {
      try {
        const response = await fetchFastApi({
          path: `/work-paper-templates/${templateId}/duplicate`,
          method: "POST",
        });

        const parsed = DuplicateTemplateResponseSchema.parse(response);
        toast.success(parsed.message);

        // Refresh the list
        await fetchTemplates();

        return parsed.templateId;
      } catch (error) {
        console.error("Error duplicating template:", error);
        toast.error("Failed to duplicate template");
        return null;
      }
    },
    [fetchTemplates],
  );

  const deleteTemplate = useCallback(
    async (templateId: string): Promise<boolean> => {
      try {
        const response = await fetchFastApi({
          path: `/work-paper-templates/${templateId}`,
          method: "DELETE",
        });

        const parsed = MessageResponseSchema.parse(response);
        toast.success(parsed.message);

        // Remove from local state
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));

        return true;
      } catch (error) {
        console.error("Error deleting template:", error);
        toast.error("Failed to delete template");
        return false;
      }
    },
    [],
  );

  useEffect(() => {
    if (fetchOnLoad) {
      fetchTemplates();
    }
  }, [fetchOnLoad, fetchTemplates]);

  return {
    loading,
    templates,
    fetchTemplates,
    duplicateTemplate,
    deleteTemplate,
  };
}
